import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { App } from "../types/app";
import type {
  AppStoreSubscriptionPeriod,
  GooglePlaySubscriptionItemInput,
  GooglePlaySubscriptionsCreateRequest,
  GooglePlaySubscriptionsCreateResponse,
  GooglePlaySubscriptionsValidationResponse,
  StoredGooglePlaySubscriptionRecord,
  SubscriptionTemplatePreset,
} from "../types/subscription";
import { useAuth } from "../hooks/useAuth";
import AIService from "../services/AIService";
import AppService from "../services/AppService";
import SubscriptionService from "../services/SubscriptionService";
import { GOOGLE_PLAY_STORE_LOCALES } from "../utils/storeLocales";
import CreationProgressModal from "../components/CreationProgressModal";

const GOOGLE_PLAY_SUBSCRIPTION_DESCRIPTION_MAX_LENGTH = 80;
const AI_LOCALE_BATCH_SIZE = 12;
const AI_BATCH_RETRY_COUNT = 2;

const TEMPLATE_OPTIONS: Array<{
  value: SubscriptionTemplatePreset;
  label: string;
}> = [
  { value: "monthly_yearly", label: "Yearly and Monthly" },
  { value: "yearly_weekly", label: "Yearly and Weekly" },
  { value: "yearly_only", label: "Only Yearly" },
  { value: "monthly_only", label: "Only Monthly" },
  { value: "custom_selection", label: "Custom Selection" },
];

const AI_PLAN_DURATION_OPTIONS: Array<{
  value: AppStoreSubscriptionPeriod;
  label: string;
}> = [
  { value: "ONE_WEEK", label: "1 week" },
  { value: "ONE_MONTH", label: "1 month" },
  { value: "ONE_YEAR", label: "1 year" },
];

const TEMPLATE_PERIODS: Record<
  SubscriptionTemplatePreset,
  AppStoreSubscriptionPeriod[]
> = {
  monthly_yearly: ["ONE_MONTH", "ONE_YEAR"],
  yearly_weekly: ["ONE_WEEK", "ONE_YEAR"],
  yearly_only: ["ONE_YEAR"],
  monthly_only: ["ONE_MONTH"],
  custom_selection: ["ONE_WEEK", "ONE_MONTH", "ONE_YEAR"],
};

const PERIOD_OPTIONS: Array<{
  value: AppStoreSubscriptionPeriod;
  label: string;
}> = [
  { value: "ONE_WEEK", label: "1 week" },
  { value: "ONE_MONTH", label: "1 month" },
  { value: "TWO_MONTHS", label: "2 months" },
  { value: "THREE_MONTHS", label: "3 months" },
  { value: "SIX_MONTHS", label: "6 months" },
  { value: "ONE_YEAR", label: "1 year" },
];

const MODELS = Array.from(
  new Set([
    AIService.getDefaultModel(),
    "gemini-2.5-flash-lite",
    "gpt-4.1-mini",
  ]),
);

const makeDraft = (): GooglePlaySubscriptionItemInput => ({
  referenceName: "",
  productId: "",
  subscriptionPeriod: "ONE_MONTH",
  basePlanId: "",
  priceAmount: "",
  currencyCode: "USD",
  localizations: [
    { locale: "en-US", title: "", description: "", benefits: [] },
  ],
});

const normalizeGooglePlayBasePlanId = (value?: string) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63);

const normalizeGooglePlayProductId = (value?: string) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._]+/g, "_")
    .replace(/^[^a-z0-9]+/, "")
    .slice(0, 40);

const getDefaultPriceByPeriod = (period: AppStoreSubscriptionPeriod) => {
  switch (period) {
    case "ONE_WEEK":
      return "2.99";
    case "ONE_MONTH":
      return "9.99";
    case "ONE_YEAR":
      return "59.99";
    default:
      return "9.99";
  }
};

const GooglePlaySubscriptionsWorkflow: React.FC = () => {
  const { user } = useAuth();
  const [apps, setApps] = React.useState<App[]>([]);
  const [selectedAppId, setSelectedAppId] = React.useState("");
  const [loadingApps, setLoadingApps] = React.useState(true);
  const [loadingRecords, setLoadingRecords] = React.useState(false);
  const [records, setRecords] = React.useState<
    StoredGooglePlaySubscriptionRecord[]
  >([]);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [validating, setValidating] = React.useState(false);
  const [generatingAI, setGeneratingAI] = React.useState(false);
  const [templatePreset, setTemplatePreset] =
    React.useState<SubscriptionTemplatePreset>("monthly_yearly");
  const [selectedPlanDurations, setSelectedPlanDurations] = React.useState<
    AppStoreSubscriptionPeriod[]
  >(TEMPLATE_PERIODS.monthly_yearly);
  const [aiModel, setAiModel] = React.useState(AIService.getDefaultModel());
  const [baseRegionCode, setBaseRegionCode] = React.useState("US");
  const [regionsVersion, setRegionsVersion] = React.useState("2022/02");
  const [includeThreeDayFreeTrial, setIncludeThreeDayFreeTrial] =
    React.useState(true);
  const [selectedLocales, setSelectedLocales] = React.useState<string[]>([
    "en-US",
  ]);
  const [localeSearchQuery, setLocaleSearchQuery] = React.useState("");
  const [subscriptions, setSubscriptions] = React.useState<
    GooglePlaySubscriptionItemInput[]
  >([makeDraft()]);
  const [validationResult, setValidationResult] =
    React.useState<GooglePlaySubscriptionsValidationResponse | null>(null);
  const [creationResult, setCreationResult] =
    React.useState<GooglePlaySubscriptionsCreateResponse | null>(null);
  const [runningOneGo, setRunningOneGo] = React.useState(false);

  const selectableApps = React.useMemo(
    () =>
      apps.filter(
        (app) => app.platform === "android" || app.platform === "both",
      ),
    [apps],
  );
  const selectedApp = React.useMemo(
    () => selectableApps.find((app) => app.appId === selectedAppId) || null,
    [selectableApps, selectedAppId],
  );
  const filteredLocales = React.useMemo(() => {
    const query = localeSearchQuery.trim().toLowerCase();
    if (!query) return GOOGLE_PLAY_STORE_LOCALES;
    return GOOGLE_PLAY_STORE_LOCALES.filter(
      (locale) =>
        locale.code.toLowerCase().includes(query) ||
        locale.name.toLowerCase().includes(query),
    );
  }, [localeSearchQuery]);

  React.useEffect(() => {
    if (!user?.email) return;
    let mounted = true;
    const load = async () => {
      try {
        setLoadingApps(true);
        const userApps = await AppService.getUserApps(user.email);
        if (!mounted) return;
        setApps(userApps);
      } catch (loadError) {
        console.error(loadError);
        if (mounted) setError("Failed to load apps.");
      } finally {
        if (mounted) setLoadingApps(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [user?.email]);

  const loadRecords = React.useCallback(async () => {
    if (!user?.email || !selectedAppId) {
      setRecords([]);
      return;
    }
    try {
      setLoadingRecords(true);
      const next =
        await SubscriptionService.listStoredGooglePlaySubscriptionRecords(
          selectedAppId,
          user.email,
        );
      setRecords(next);
    } catch (loadError) {
      console.error(loadError);
      setError("Failed to load Google Play subscription records.");
    } finally {
      setLoadingRecords(false);
    }
  }, [selectedAppId, user?.email]);

  React.useEffect(() => {
    void loadRecords();
  }, [loadRecords]);

  React.useEffect(() => {
    setSubscriptions((current) =>
      current.map((item) => {
        const byLocale = new Map(
          (Array.isArray(item.localizations) ? item.localizations : []).map(
            (entry) => [entry.locale, entry],
          ),
        );
        return {
          ...item,
          localizations: selectedLocales.map((locale) => ({
            locale,
            title: byLocale.get(locale)?.title || "",
            description: byLocale.get(locale)?.description || "",
            benefits: Array.isArray(byLocale.get(locale)?.benefits)
              ? byLocale
                  .get(locale)!
                  .benefits!.map((benefit) => String(benefit || "").trim())
                  .filter(Boolean)
                  .slice(0, 4)
              : [],
          })),
        };
      }),
    );
  }, [selectedLocales]);

  const payload =
    React.useMemo<GooglePlaySubscriptionsCreateRequest | null>(() => {
      if (!selectedAppId) return null;
      return {
        appId: selectedAppId,
        baseRegionCode: baseRegionCode.trim().toUpperCase(),
        regionsVersion: regionsVersion.trim(),
        includeThreeDayFreeTrial,
        subscriptions: subscriptions.map((item) => ({
          ...item,
          referenceName: String(item.referenceName || "").trim(),
          productId: normalizeGooglePlayProductId(item.productId),
          basePlanId: normalizeGooglePlayBasePlanId(item.basePlanId),
          priceAmount: String(item.priceAmount || "").trim(),
          currencyCode: String(item.currencyCode || "USD")
            .trim()
            .toUpperCase(),
          localizations: (Array.isArray(item.localizations)
            ? item.localizations
            : []
          ).map((entry) => ({
            locale: String(entry.locale || "").trim(),
            title: String(entry.title || "").trim(),
            description: String(entry.description || "")
              .trim()
              .slice(0, GOOGLE_PLAY_SUBSCRIPTION_DESCRIPTION_MAX_LENGTH),
            benefits: Array.isArray(entry.benefits)
              ? entry.benefits
                  .map((benefit) => String(benefit || "").trim())
                  .filter(Boolean)
                  .slice(0, 4)
              : [],
          })),
        })),
      };
    }, [
      baseRegionCode,
      includeThreeDayFreeTrial,
      regionsVersion,
      selectedAppId,
      subscriptions,
    ]);

  const updateSub = (
    index: number,
    updater: (
      item: GooglePlaySubscriptionItemInput,
    ) => GooglePlaySubscriptionItemInput,
  ) => {
    setSubscriptions((current) =>
      current.map((item, idx) => (idx === index ? updater(item) : item)),
    );
  };

  const handleGenerateAI = async () => {
    if (!selectedApp) {
      setError("Select an app before AI generation.");
      return;
    }
    try {
      setGeneratingAI(true);
      setError("");
      setSuccess("");
      const requiredPeriods = AI_PLAN_DURATION_OPTIONS.map(
        (option) => option.value,
      ).filter((period) => selectedPlanDurations.includes(period));
      if (!requiredPeriods.length) {
        setError("Select at least one duration before AI generation.");
        return;
      }
      const corePrompt = [
        "Create Google Play subscription products JSON for Android app.",
        `App: ${selectedApp.name}`,
        `Package hint: ${selectedApp.googlePlayPackageName || selectedApp.storeId || selectedApp.appId}`,
        `Required subscription periods: ${requiredPeriods.join(", ")}`,
        "Use USD prices and practical IDs.",
        "Do not include localizations in this step.",
        "productId must use lowercase letters, numbers, dots, and underscores only (max 40 chars). Do not include long package-name prefixes.",
        "basePlanId must use lowercase letters, numbers, and hyphens only (no underscores).",
        "Return JSON: { subscriptions: [{ referenceName, productId, subscriptionPeriod, basePlanId, priceAmount, currencyCode }] }",
      ].join("\n");
      const coreSchema = {
        type: "object",
        properties: {
          subscriptions: {
            type: "array",
            items: {
              type: "object",
              properties: {
                referenceName: { type: "string" },
                productId: { type: "string" },
                subscriptionPeriod: { type: "string" },
                basePlanId: { type: "string" },
                priceAmount: { type: "string" },
                currencyCode: { type: "string" },
              },
              required: [
                "referenceName",
                "productId",
                "subscriptionPeriod",
                "basePlanId",
                "priceAmount",
                "currencyCode",
              ],
            },
          },
        },
        required: ["subscriptions"],
      };

      const coreResult = await AIService.generateJSON<any>(corePrompt, {
        model: aiModel,
        temperature: 0.3,
        maxTokens: 1200,
        structuredOutputSchema: coreSchema,
        structuredOutputName: "google_play_subscriptions_core",
      });
      const aiSubscriptions = Array.isArray(coreResult?.subscriptions)
        ? coreResult.subscriptions
        : [];
      if (!aiSubscriptions.length) {
        throw new Error("AI did not return any subscriptions.");
      }

      const periodSet = new Set(requiredPeriods);
      const byPeriod = new Map<AppStoreSubscriptionPeriod, GooglePlaySubscriptionItemInput>();

      aiSubscriptions.forEach((item: any) => {
        const period = String(
          item?.subscriptionPeriod || "ONE_MONTH",
        ).trim() as AppStoreSubscriptionPeriod;
        if (!periodSet.has(period) || byPeriod.has(period)) return;

        const fallbackSuffix = period.toLowerCase();
        byPeriod.set(period, {
          referenceName:
            String(item?.referenceName || "").trim() ||
            `${selectedApp.name} ${period.replace("_", " ").toLowerCase()}`,
          productId:
            normalizeGooglePlayProductId(item?.productId) ||
            normalizeGooglePlayProductId(`${selectedApp.name}_${fallbackSuffix}`),
          subscriptionPeriod: period,
          basePlanId:
            normalizeGooglePlayBasePlanId(item?.basePlanId) ||
            normalizeGooglePlayBasePlanId(fallbackSuffix),
          priceAmount:
            String(item?.priceAmount || "").trim() || getDefaultPriceByPeriod(period),
          currencyCode: String(item?.currencyCode || "USD")
            .trim()
            .toUpperCase(),
          localizations: [],
        });
      });

      requiredPeriods.forEach((period) => {
        if (byPeriod.has(period)) return;
        const fallbackSuffix = period.toLowerCase();
        byPeriod.set(period, {
          referenceName: `${selectedApp.name} ${period
            .replace("_", " ")
            .toLowerCase()}`,
          productId: normalizeGooglePlayProductId(
            `${selectedApp.name}_${fallbackSuffix}`,
          ),
          subscriptionPeriod: period,
          basePlanId: normalizeGooglePlayBasePlanId(fallbackSuffix),
          priceAmount: getDefaultPriceByPeriod(period),
          currencyCode: "USD",
          localizations: [],
        });
      });

      const normalizedSubscriptions = requiredPeriods
        .map((period) => byPeriod.get(period))
        .filter(
          (
            item,
          ): item is GooglePlaySubscriptionItemInput => Boolean(item),
        );

      const chunkedLocales: string[][] = [];
      for (
        let index = 0;
        index < selectedLocales.length;
        index += AI_LOCALE_BATCH_SIZE
      ) {
        chunkedLocales.push(
          selectedLocales.slice(index, index + AI_LOCALE_BATCH_SIZE),
        );
      }

      const localizedByProduct = new Map<
        string,
        Map<string, { title: string; description: string; benefits: string[] }>
      >();

      const productsForPrompt = normalizedSubscriptions
        .map(
          (subscription) =>
            `- productId: ${subscription.productId}; referenceName: ${subscription.referenceName}; subscriptionPeriod: ${subscription.subscriptionPeriod}`,
        )
        .join("\n");

      const localizationSchema = {
        type: "object",
        properties: {
          localizationsByProductId: {
            type: "object",
            additionalProperties: {
              type: "object",
              additionalProperties: {
                type: "object",
                properties: {
                  title: { type: "string" },
                  description: { type: "string" },
                  benefits: {
                    type: "array",
                    items: { type: "string" },
                  },
                },
                required: ["title", "description"],
              },
            },
          },
        },
        required: ["localizationsByProductId"],
      };

      const requestLocalizationBatch = async (localeBatch: string[]) => {
        const localizationPrompt = [
          "Localize Google Play subscription copy for the provided products and locales.",
          `App: ${selectedApp.name}`,
          `Target locales: ${localeBatch.join(", ")}`,
          "Each localization description must be 80 characters or fewer.",
          "For each localization, include benefits as an array of short plain-text strings (max 4 items).",
          "Use exactly the provided product IDs and locale codes.",
          "Products:",
          productsForPrompt,
          "Return JSON: { localizationsByProductId: { [productId]: { [locale]: { title, description, benefits[] } } } }",
        ].join("\n");

        let batchResult: any = null;
        let batchError: unknown = null;
        for (let attempt = 1; attempt <= AI_BATCH_RETRY_COUNT; attempt += 1) {
          try {
            batchResult = await AIService.generateJSON<any>(localizationPrompt, {
              model: aiModel,
              temperature: 0.3,
              maxTokens: 2400,
              structuredOutputSchema: localizationSchema,
              structuredOutputName: "google_play_subscriptions_localizations",
            });
            batchError = null;
            break;
          } catch (error) {
            batchError = error;
          }
        }

        if (batchError) {
          throw new Error(
            `Failed to generate localizations for locales (${localeBatch.join(
              ", ",
            )}): ${
              batchError instanceof Error ? batchError.message : "Unknown AI error"
            }`,
          );
        }

        return batchResult;
      };

      for (const localeBatch of chunkedLocales) {
        let batchResult: any;
        try {
          batchResult = await requestLocalizationBatch(localeBatch);
        } catch (batchError) {
          const fallbackByProductId: Record<string, Record<string, any>> = {};
          for (const locale of localeBatch) {
            const singleLocaleResult = await requestLocalizationBatch([locale]);
            const singleByProductId =
              singleLocaleResult?.localizationsByProductId &&
              typeof singleLocaleResult.localizationsByProductId === "object"
                ? singleLocaleResult.localizationsByProductId
                : {};

            Object.entries(singleByProductId).forEach(([productId, localeMap]) => {
              if (!fallbackByProductId[productId]) {
                fallbackByProductId[productId] = {};
              }
              if (localeMap && typeof localeMap === "object") {
                Object.entries(localeMap as Record<string, any>).forEach(
                  ([localeCode, localization]) => {
                    fallbackByProductId[productId][localeCode] = localization;
                  },
                );
              }
            });
          }
          batchResult = { localizationsByProductId: fallbackByProductId };
          console.warn(
            "Locale batch fallback to single-locale generation.",
            localeBatch,
            batchError,
          );
        }

        const byProductId =
          batchResult?.localizationsByProductId &&
          typeof batchResult.localizationsByProductId === "object"
            ? batchResult.localizationsByProductId
            : {};

        normalizedSubscriptions.forEach((subscription) => {
          const productId = subscription.productId;
          const perLocaleRaw =
            byProductId && typeof byProductId === "object"
              ? byProductId[productId]
              : null;
          if (!localizedByProduct.has(productId)) {
            localizedByProduct.set(productId, new Map());
          }
          const destination = localizedByProduct.get(productId)!;

          localeBatch.forEach((locale) => {
            const localized =
              perLocaleRaw && typeof perLocaleRaw === "object"
                ? perLocaleRaw[locale]
                : null;
            destination.set(locale, {
              title: String(localized?.title || selectedApp.name).trim(),
              description: String(
                localized?.description || `Premium access for ${selectedApp.name}`,
              )
                .trim()
                .slice(0, GOOGLE_PLAY_SUBSCRIPTION_DESCRIPTION_MAX_LENGTH),
              benefits: Array.isArray(localized?.benefits)
                ? localized?.benefits
                    .map((benefit: any) => String(benefit || "").trim())
                    .filter(Boolean)
                    .slice(0, 4)
                : [],
            });
          });
        });
      }

      setSubscriptions(
        normalizedSubscriptions.map((subscription) => ({
          ...subscription,
          localizations: selectedLocales.map((locale) => {
            const localized = localizedByProduct
              .get(subscription.productId)
              ?.get(locale);
            return {
              locale,
              title: String(localized?.title || selectedApp.name).trim(),
              description: String(
                localized?.description ||
                  `Premium access for ${selectedApp.name}`,
              )
                .trim()
                .slice(0, GOOGLE_PLAY_SUBSCRIPTION_DESCRIPTION_MAX_LENGTH),
              benefits: Array.isArray(localized?.benefits)
                ? (localized?.benefits || [])
                    .map((benefit: any) => String(benefit || "").trim())
                    .filter(Boolean)
                    .slice(0, 4)
                : [],
            };
          }),
        })),
      );
      setSuccess("AI generated Google Play subscription drafts.");
    } catch (aiError) {
      console.error(aiError);
      setError(
        aiError instanceof Error ? aiError.message : "AI generation failed.",
      );
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleValidate = async () => {
    if (!payload) {
      setError("Select an app before validation.");
      return;
    }
    try {
      setValidating(true);
      setError("");
      const result =
        await SubscriptionService.validateGooglePlaySubscriptions(payload);
      setValidationResult(result);
      if (result.validationIssues.some((item) => item.severity === "error")) {
        setError("Fix validation issues before creating.");
      } else {
        setSuccess("Google Play subscription payload validated.");
      }
    } catch (validateError) {
      console.error(validateError);
      setError(
        validateError instanceof Error
          ? validateError.message
          : "Validation failed.",
      );
    } finally {
      setValidating(false);
    }
  };

  const handleCreate = async (options?: { validateFirst?: boolean }) => {
    if (!payload) {
      setError("Select an app before creating subscriptions.");
      return;
    }
    try {
      const shouldValidate = options?.validateFirst ?? true;
      if (shouldValidate) {
        const validation =
          await SubscriptionService.validateGooglePlaySubscriptions(payload);
        setValidationResult(validation);
        const hasBlockingErrors = validation.validationIssues.some(
          (item) => item.severity === "error",
        );
        if (hasBlockingErrors) {
          setError("Validation blocked creation. Fix issues and retry.");
          return;
        }
      }
      setCreating(true);
      setError("");
      const result =
        await SubscriptionService.createGooglePlaySubscriptions(payload);
      setCreationResult(result);
      setSuccess(result.message);
      await loadRecords();
    } catch (createError) {
      console.error(createError);
      setError(
        createError instanceof Error ? createError.message : "Create failed.",
      );
    } finally {
      setCreating(false);
    }
  };

  const handleOneGo = async () => {
    if (!selectedAppId) {
      setError("Select an app before running one-go create.");
      return;
    }
    try {
      setRunningOneGo(true);
      setError("");
      setSuccess("");
      await handleCreate({ validateFirst: true });
    } finally {
      setRunningOneGo(false);
    }
  };

  if (loadingApps) {
    return (
      <Box sx={{ minHeight: "40vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2.5}>
      <CreationProgressModal
        open={creating}
        storeLabel="Google Play"
        title="Creating subscriptions"
        description="We are creating subscriptions and activating base plans in Google Play. Please keep this page open until processing finishes."
      />
      {error ? <Alert severity="error">{error}</Alert> : null}
      {success ? <Alert severity="success">{success}</Alert> : null}
      <Alert severity="info">
        <strong>How this workflow works:</strong> select app and locales,
        generate drafts with AI, review IDs/prices/localized text, then run{" "}
        <strong>Validate + Create</strong>. The system validates first, creates
        subscriptions and base plans, then activates the base plans.
      </Alert>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Google Play Subscription One-Go Workflow
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure once, generate with AI, then run validate and create in
              one action.
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip size="small" color="primary" label="1. Setup" />
              <Chip size="small" color="primary" label="2. Generate or Edit" />
              <Chip size="small" color="primary" label="3. Validate + Create" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Step 1: Setup
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose app, template, AI model, and pricing region defaults.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Registered app</InputLabel>
                  <Select
                    value={selectedAppId}
                    label="Registered app"
                    onChange={(event) =>
                      setSelectedAppId(String(event.target.value))
                    }
                  >
                    <MenuItem value="">
                      <em>Choose your app</em>
                    </MenuItem>
                    {selectableApps.map((app) => (
                      <MenuItem key={app.appId} value={app.appId}>
                        {app.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Template</InputLabel>
                  <Select
                    value={templatePreset}
                    label="Template"
                    onChange={(event) => {
                      const nextTemplate = event.target
                        .value as SubscriptionTemplatePreset;
                      setTemplatePreset(nextTemplate);
                      setSelectedPlanDurations(TEMPLATE_PERIODS[nextTemplate]);
                    }}
                  >
                    {TEMPLATE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
                  Plan durations for AI generation
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {AI_PLAN_DURATION_OPTIONS.map((option) => {
                    const checked = selectedPlanDurations.includes(
                      option.value,
                    );
                    return (
                      <Chip
                        key={option.value}
                        label={option.label}
                        color={checked ? "primary" : "default"}
                        variant={checked ? "filled" : "outlined"}
                        onClick={() => {
                          setSelectedPlanDurations((current) => {
                            const next = current.includes(option.value)
                              ? current.filter((item) => item !== option.value)
                              : [...current, option.value];
                            return AI_PLAN_DURATION_OPTIONS.map(
                              (item) => item.value,
                            ).filter((period) => next.includes(period));
                          });
                        }}
                      />
                    );
                  })}
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>AI model</InputLabel>
                  <Select
                    value={aiModel}
                    label="AI model"
                    onChange={(event) => setAiModel(String(event.target.value))}
                  >
                    {MODELS.map((model) => (
                      <MenuItem key={model} value={model}>
                        {model}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Base region"
                  value={baseRegionCode}
                  onChange={(event) => setBaseRegionCode(event.target.value)}
                  helperText="2-letter region used for initial base plan pricing (for example US)."
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Regions version"
                  value={regionsVersion}
                  onChange={(event) => setRegionsVersion(event.target.value)}
                  helperText="Google Play regions schema version used for regional price configs."
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeThreeDayFreeTrial}
                      onChange={(_event, checked) =>
                        setIncludeThreeDayFreeTrial(checked)
                      }
                    />
                  }
                  label="Auto-attach 3-day free trial after creation"
                />
                <Typography variant="body2" color="text.secondary">
                  When enabled, each created base plan gets a trial offer
                  automatically created and activated.
                </Typography>
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Step 2: Choose Locales
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select all locales to generate and publish localized subscription
              text.
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip
                label={`Selected locales: ${selectedLocales.length}`}
                size="small"
              />
              <Chip
                label={`Subscriptions: ${subscriptions.length}`}
                size="small"
              />
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.25}>
              <TextField
                size="small"
                label="Find locale"
                placeholder="Search code or language"
                value={localeSearchQuery}
                onChange={(event) => setLocaleSearchQuery(event.target.value)}
                sx={{ minWidth: { xs: "100%", md: 280 } }}
              />
              <Button
                variant="outlined"
                onClick={() =>
                  setSelectedLocales((current) =>
                    Array.from(
                      new Set([
                        ...current,
                        ...filteredLocales.map((locale) => locale.code),
                      ]),
                    ),
                  )
                }
              >
                Select Visible
              </Button>
              <Button
                variant="text"
                onClick={() =>
                  setSelectedLocales((current) =>
                    current.filter(
                      (code) =>
                        !filteredLocales.some((locale) => locale.code === code),
                    ),
                  )
                }
              >
                Clear Visible
              </Button>
            </Stack>
            <FormGroup
              sx={{
                maxHeight: 220,
                overflow: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 1,
              }}
            >
              {filteredLocales.map((locale) => (
                <FormControlLabel
                  key={locale.code}
                  control={
                    <Checkbox
                      checked={selectedLocales.includes(locale.code)}
                      onChange={() =>
                        setSelectedLocales((current) =>
                          current.includes(locale.code)
                            ? current.filter((item) => item !== locale.code)
                            : [...current, locale.code],
                        )
                      }
                    />
                  }
                  label={`${locale.flag} ${locale.name} (${locale.code})`}
                />
              ))}
            </FormGroup>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Step 3: Generate and Run
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generate drafts with AI, then validate and create. Use manual
              actions if needed.
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.25}>
              <Button
                variant="contained"
                startIcon={
                  generatingAI ? (
                    <CircularProgress size={16} />
                  ) : (
                    <AutoAwesomeIcon />
                  )
                }
                disabled={generatingAI || !selectedApp}
                onClick={handleGenerateAI}
              >
                {generatingAI ? "Generating..." : "Generate With AI"}
              </Button>
              <Button
                variant="contained"
                color="success"
                disabled={
                  runningOneGo || creating || validating || !selectedAppId
                }
                onClick={handleOneGo}
              >
                {runningOneGo || creating
                  ? "Validating + Creating..."
                  : "Validate + Create"}
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  setSubscriptions((current) => [...current, makeDraft()])
                }
              >
                Add subscription
              </Button>
              <Button
                variant="outlined"
                disabled={validating || creating || !selectedAppId}
                onClick={handleValidate}
              >
                {validating ? "Validating..." : "Validate only"}
              </Button>
              <Button
                variant="outlined"
                disabled={creating || validating || !selectedAppId}
                onClick={() => void handleCreate({ validateFirst: false })}
              >
                {creating ? "Creating..." : "Create only (skip validation)"}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        Step 4: Review Subscription Details
      </Typography>
      {subscriptions.map((subscription, index) => (
        <Card
          key={`${subscription.productId}_${index}`}
          sx={{ borderRadius: 3 }}
        >
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Typography sx={{ fontWeight: 700 }}>
                Subscription {index + 1}
              </Typography>
              <Chip size="small" variant="outlined" label="Required fields" />
              <Button
                size="small"
                color="error"
                disabled={subscriptions.length === 1}
                onClick={() =>
                  setSubscriptions((current) =>
                    current.filter((_, itemIndex) => itemIndex !== index),
                  )
                }
              >
                Remove
              </Button>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Reference name"
                  value={subscription.referenceName}
                  onChange={(event) =>
                    updateSub(index, (current) => ({
                      ...current,
                      referenceName: event.target.value,
                    }))
                  }
                  helperText="Internal readable name for your team."
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Product ID"
                  value={subscription.productId}
                  onChange={(event) =>
                    updateSub(index, (current) => ({
                      ...current,
                      productId: event.target.value,
                    }))
                  }
                  helperText="Lowercase letters, numbers, dots, and underscores only. Max 40 chars."
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Billing period</InputLabel>
                  <Select
                    value={subscription.subscriptionPeriod}
                    label="Billing period"
                    onChange={(event) =>
                      updateSub(index, (current) => ({
                        ...current,
                        subscriptionPeriod: event.target
                          .value as AppStoreSubscriptionPeriod,
                      }))
                    }
                  >
                    {PERIOD_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Base plan ID"
                  value={subscription.basePlanId || ""}
                  onChange={(event) =>
                    updateSub(index, (current) => ({
                      ...current,
                      basePlanId: event.target.value,
                    }))
                  }
                  helperText="Lowercase letters, numbers, and hyphens only (for example monthly-basic)."
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Currency"
                  value={subscription.currencyCode}
                  onChange={(event) =>
                    updateSub(index, (current) => ({
                      ...current,
                      currencyCode: event.target.value.toUpperCase(),
                    }))
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Base price"
                  value={subscription.priceAmount}
                  onChange={(event) =>
                    updateSub(index, (current) => ({
                      ...current,
                      priceAmount: event.target.value,
                    }))
                  }
                  placeholder="4.99"
                  helperText="Price in the selected currency for base region."
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Localized copy used for each selected Google Play locale.
                </Typography>
                <Grid container spacing={1.25}>
                  {subscription.localizations.map((entry) => (
                    <Grid item xs={12} md={6} key={`${index}_${entry.locale}`}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 1.25, borderRadius: 2 }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          {entry.locale}
                        </Typography>
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          <TextField
                            label="Title"
                            value={entry.title}
                            onChange={(event) =>
                              updateSub(index, (current) => ({
                                ...current,
                                localizations: current.localizations.map(
                                  (item) =>
                                    item.locale === entry.locale
                                      ? { ...item, title: event.target.value }
                                      : item,
                                ),
                              }))
                            }
                            size="small"
                            fullWidth
                          />
                          <TextField
                            label="Description"
                            value={entry.description}
                            onChange={(event) =>
                              updateSub(index, (current) => ({
                                ...current,
                                localizations: current.localizations.map(
                                  (item) =>
                                    item.locale === entry.locale
                                      ? {
                                          ...item,
                                          description: event.target.value.slice(
                                            0,
                                            GOOGLE_PLAY_SUBSCRIPTION_DESCRIPTION_MAX_LENGTH,
                                          ),
                                        }
                                      : item,
                                ),
                              }))
                            }
                            size="small"
                            multiline
                            minRows={2}
                            helperText={`${entry.description.length}/${GOOGLE_PLAY_SUBSCRIPTION_DESCRIPTION_MAX_LENGTH} characters`}
                            fullWidth
                          />
                          <TextField
                            label="Benefits (one per line, up to 4)"
                            value={(entry.benefits || []).join("\n")}
                            onChange={(event) =>
                              updateSub(index, (current) => ({
                                ...current,
                                localizations: current.localizations.map(
                                  (item) =>
                                    item.locale === entry.locale
                                      ? {
                                          ...item,
                                          benefits: event.target.value
                                            .split("\n")
                                            .map((benefit) => benefit.trim())
                                            .filter(Boolean)
                                            .slice(0, 4),
                                        }
                                      : item,
                                ),
                              }))
                            }
                            size="small"
                            multiline
                            minRows={2}
                            fullWidth
                          />
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        Step 5: Review Validation and Creation Results
      </Typography>
      {validationResult ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 1 }}
          >
            <Chip label={`Package: ${validationResult.packageName}`} />
            <Chip label={`Plans: ${validationResult.results.length}`} />
          </Stack>
          {validationResult.validationIssues.length === 0 ? (
            <Alert severity="success">No validation issues detected.</Alert>
          ) : (
            <Stack spacing={1}>
              {validationResult.validationIssues.map((issue, index) => (
                <Alert
                  key={`${issue.field}_${index}`}
                  severity={issue.severity === "warning" ? "warning" : "error"}
                >
                  {issue.message}
                </Alert>
              ))}
            </Stack>
          )}
        </Paper>
      ) : null}

      {creationResult ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>
            {creationResult.message}
          </Typography>
          <Stack spacing={1}>
            {creationResult.subscriptions.map((item) => (
              <Alert
                key={`${item.productId}_${item.basePlanId}`}
                severity={item.status === "created" ? "success" : "warning"}
              >
                {item.productId}: {item.message}
              </Alert>
            ))}
          </Stack>
        </Paper>
      ) : null}

      <Accordion defaultExpanded={false}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 700 }}>Saved Records</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {loadingRecords ? (
            <CircularProgress size={20} />
          ) : records.length === 0 ? (
            <Alert severity="info">
              No Google Play subscription records yet.
            </Alert>
          ) : (
            <Stack spacing={1}>
              {records.map((record) => (
                <Paper
                  key={record.recordId}
                  variant="outlined"
                  sx={{ p: 1.25, borderRadius: 2 }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>
                        {record.packageName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {record.subscriptions.length} products • Updated{" "}
                        {record.updatedAt.toLocaleString()}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      color="error"
                      onClick={async () => {
                        await SubscriptionService.deleteStoredGooglePlaySubscriptionRecord(
                          record.recordId,
                        );
                        await loadRecords();
                      }}
                    >
                      Delete
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

export default GooglePlaySubscriptionsWorkflow;
